import useStateRef from '../../hooks/useStateRef';
import { useCallback, useEffect, useMemo } from 'react';
import { BackendEvent, BackendEventTypes, DocumentEvent, DocumentEventTypes } from '../../models/BackendEvents';
import { useChat } from '../../contexts/chat/ChatContext';

const WHITELISTED_DOCUMENTS_EVENTS = [DocumentEventTypes.created, DocumentEventTypes.processed, DocumentEventTypes.failed] as BackendEventTypes[];

const getRelevantEvents = <T extends { type: BackendEventTypes; }[]>(events: T, whitelistedEvents: string[]) => {
    const filteredEvents = events.filter((event) => whitelistedEvents.includes(event.type));
    return (filteredEvents ?? []) as BackendEvent[];
};

const useDocumentEvents = () => {
    const { events: { document: documentEvents } } = useChat();
    const [events, setEvents] = useStateRef<Record<string, DocumentEvent>>({});
    const eventDictionary = useMemo<Record<string, DocumentEvent>>(() => Object.fromEntries(Object.entries(events).map(([k, v]) => [k, v])), [events]);

    const onDocumentEvent = useCallback((model: DocumentEvent) => {
        const events = getRelevantEvents([model], WHITELISTED_DOCUMENTS_EVENTS);
        if (!events?.length) return;

        events.forEach((event) => {
            const documentEvent = event as DocumentEvent;
            const handler = (prev: Record<string, DocumentEvent>) => ({ ...prev, [documentEvent.document_id]: event as DocumentEvent });
            setEvents(handler);
        });
    }, [setEvents]);

    useEffect(() => {
        const events = documentEvents;
        events.on(onDocumentEvent);
        return () => events.off(onDocumentEvent);
    }, [documentEvents, onDocumentEvent]);


    return { events, eventDictionary };
};

export default useDocumentEvents;